import type { LookupOption } from '@vision360/contracts';
import { Check, X } from 'lucide-react';
import { type KeyboardEvent, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { useLookup } from './api';

/**
 * A config field picked from a connector lookup (step 36a, PRD VS-025): a
 * combobox searching `GET /connectors/:id/lookups/:name` 200 ms after the last
 * keystroke. On an array field each option's value is one item (toggled on and
 * off, up to `maxItems`); on a scalar field the pick replaces the value.
 * Picked items show as removable chips.
 */

const DEBOUNCE_MS = 200;
const keyOf = (value: unknown) => JSON.stringify(value);

/** What a chip says for an item the user did not pick in this session. */
export function itemLabel(item: unknown): string {
  if (typeof item === 'string') return item;
  if (
    typeof item === 'object' &&
    item !== null &&
    'name' in item &&
    typeof item.name === 'string'
  ) {
    return item.name;
  }
  return JSON.stringify(item);
}

export function LookupField({
  connectorId,
  lookup,
  schema,
  label,
  optional,
  help,
  error,
  value,
  onChange,
}: {
  connectorId: string;
  lookup: string;
  schema: Record<string, unknown>;
  label: string;
  optional: boolean;
  help?: string | undefined;
  error?: string | undefined;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const { t } = useTranslation('sources');
  const id = useId();
  const listId = `${id}-list`;
  const multiple = schema['type'] === 'array';
  const max = multiple && typeof schema['maxItems'] === 'number' ? schema['maxItems'] : undefined;
  const items: unknown[] = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value === undefined
      ? []
      : [value];
  const picked = new Set(items.map(keyOf));
  const full = max !== undefined && items.length >= max;

  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  // Labels of options picked in this session, so a chip reads "Tram A" rather than "A".
  const [labels, setLabels] = useState<ReadonlyMap<string, string>>(new Map());

  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const results = useLookup(connectorId, lookup, search, open);
  const options = results.data?.options ?? [];
  const activeIndex = Math.min(active, options.length - 1);
  const optionId = (i: number) => `${id}-option-${i}`;

  // A new result list starts at its first option.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on each new result set only.
  useEffect(() => setActive(0), [results.data]);

  const remove = (index: number) =>
    onChange(multiple ? items.filter((_, i) => i !== index) : undefined);

  const pick = (option: LookupOption) => {
    const key = keyOf(option.value);
    setLabels((m) => new Map(m).set(key, option.label));
    if (!multiple) {
      onChange(option.value);
      setOpen(false);
      setQuery('');
      return;
    }
    if (picked.has(key)) return remove(items.findIndex((item) => keyOf(item) === key));
    if (full) return;
    onChange([...items, option.value]);
    setQuery('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) setOpen(true);
        else setActive(Math.min(activeIndex + 1, options.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive(Math.max(activeIndex - 1, 0));
        break;
      case 'Enter': {
        // Enter picks; it never submits the dialog from inside the picker.
        const option = open ? options[activeIndex] : undefined;
        if (open) e.preventDefault();
        if (option) pick(option);
        break;
      }
      case 'Escape':
        if (open) {
          // Peels one layer: the list closes, the dialog stays (see SourceDialog).
          e.preventDefault();
          setOpen(false);
        }
        break;
      case 'Backspace':
        if (query === '' && items.length > 0) remove(items.length - 1);
        break;
    }
  };

  const describedBy =
    [help ? `${id}-help` : '', error ? `${id}-error` : '', full ? `${id}-full` : '']
      .filter(Boolean)
      .join(' ') || undefined;

  let status: React.ReactNode = null;
  if (open && results.isError) {
    status = <span className="text-destructive">{t('lookup.error')}</span>;
  } else if (open && results.isPending) {
    status = (
      <span className="flex items-center gap-2 text-muted-foreground">
        <Spinner className="h-3 w-3" />
        {t('lookup.searching')}
      </span>
    );
  } else if (open && results.isSuccess && options.length === 0) {
    status = <span className="text-muted-foreground">{t('lookup.empty')}</span>;
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <Label htmlFor={id}>
          {label}
          {optional && (
            <span className="font-normal text-muted-foreground"> ({t('form.optional')})</span>
          )}
        </Label>
        {max !== undefined && (
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            {t('form.count', { count: items.length, max })}
          </span>
        )}
      </div>

      {items.length > 0 && (
        <ul aria-label={label} className="flex flex-wrap gap-1.5">
          {items.map((item, i) => {
            const text = labels.get(keyOf(item)) ?? itemLabel(item);
            return (
              <li
                key={keyOf(item)}
                className="animate-enter inline-flex max-w-full items-center gap-1 rounded-md border bg-muted py-0.5 pl-2 pr-0.5 text-sm"
              >
                <span className="truncate">{text}</span>
                <button
                  type="button"
                  className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
                  aria-label={t('lookup.remove', { label: text })}
                  onClick={() => remove(i)}
                >
                  <X aria-hidden="true" className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <Input
        id={id}
        role="combobox"
        autoComplete="off"
        spellCheck={false}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        placeholder={t('lookup.placeholder')}
        disabled={full}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onClick={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />

      {open && options.length > 0 && (
        <div
          id={listId}
          role="listbox"
          aria-label={label}
          aria-multiselectable={multiple || undefined}
          className="animate-enter max-h-64 overflow-y-auto rounded-md border bg-card p-1 shadow-lg"
        >
          {options.map((option, i) => {
            const selected = picked.has(keyOf(option.value));
            return (
              // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard goes through the combobox (aria-activedescendant).
              <div
                key={keyOf(option.value)}
                id={optionId(i)}
                role="option"
                tabIndex={-1}
                aria-selected={selected}
                data-active={i === activeIndex || undefined}
                // The active option carries the brand rail (brand skill §3), like the palette.
                className="relative flex cursor-pointer items-start gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors duration-[90ms] before:absolute before:inset-y-1.5 before:left-0 before:w-0.5 before:rounded-full before:bg-primary before:opacity-0 data-active:bg-muted data-active:before:opacity-100"
                // Keeps focus in the input, so the list stays open for the next pick.
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(option)}
              >
                <Check
                  aria-hidden="true"
                  className={cn('mt-0.5 h-4 w-4 shrink-0 text-primary', !selected && 'invisible')}
                />
                <span className="min-w-0">
                  <span className="block truncate">{option.label}</span>
                  {option.description && (
                    <span className="block truncate font-mono text-xs text-muted-foreground">
                      {option.description}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <output aria-live="polite" className="block text-xs empty:hidden">
        {status}
      </output>
      {full && (
        <p id={`${id}-full`} className="text-xs text-muted-foreground">
          {t('lookup.full', { max })}
        </p>
      )}
      {help && (
        <p id={`${id}-help`} className="text-xs text-muted-foreground">
          {help}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
